/**
 * CrisisDetectionService.ts
 *
 * Advanced crisis detection service supporting multiple crisis definition modes:
 * 1. Traditional negative return crisis (existing logic)
 * 2. Capital index drawdown crisis with recovery tracking (new implementation)
 */

import { CrisisDefinitionType, CrisisState } from '../types/simulation';

export class CrisisDetectionService {
  private crisisDefinition: CrisisDefinitionType;
  private crisisState: CrisisState;
  private kapitalIndexPeaks: number[] = []; // Track historical peaks for drawdown detection

  constructor(crisisDefinition: CrisisDefinitionType = 'negative-return') {
    this.crisisDefinition = crisisDefinition;
    // Initialize crisis state before calling resetCrisisState
    this.crisisState = {
      isInCrisis: false,
      crisisStartYear: 0,
      precrisisKapitalIndex: 100,
      crisisType: crisisDefinition
    };
    this.resetCrisisState();
  }

  /**
   * Main crisis evaluation method
   * Determines if current year should be marked as crisis based on selected definition
   */
  public evaluateCrisis(
    currentYear: number,
    investitionstopfRate: number,
    previousKapitalIndex: number = 100,
    currentKapitalIndex: number
  ): boolean {
    // Store capital index for historical tracking
    this.updateKapitalIndexHistory(currentYear, currentKapitalIndex);

    if (this.crisisDefinition === 'negative-return') {
      return this.detectNegativeReturnCrisis(investitionstopfRate, currentYear, previousKapitalIndex);
    } else if (this.crisisDefinition === 'kapitalindex-drawdown') {
      return this.detectKapitalIndexDrawdownCrisis(
        currentYear,
        currentKapitalIndex,
        previousKapitalIndex
      );
    }

    // Fallback to traditional crisis detection
    return this.detectNegativeReturnCrisis(investitionstopfRate, currentYear, previousKapitalIndex);
  }

  /**
   * Evaluate crisis status based on start-of-year capital index
   * This method is called at the beginning of each year for strategy decisions
   * TIMING FIX: Decisions are based on information available at start of year
   */
  public evaluateCrisisStartOfYear(
    currentYear: number,
    startOfYearKapitalIndex: number
  ): boolean {
    if (this.crisisDefinition === 'negative-return') {
      // For negative return crisis, we can't evaluate at start of year
      // Keep existing crisis state
      return this.crisisState.isInCrisis;
    } else if (this.crisisDefinition === 'kapitalindex-drawdown') {
      return this.detectKapitalIndexDrawdownCrisisStartOfYear(
        currentYear,
        startOfYearKapitalIndex
      );
    }

    // Fallback: keep existing crisis state
    return this.crisisState.isInCrisis;
  }

  /**
   * Update peak tracking at end of year
   * This method should be called after all transactions are complete
   */
  public updateEndOfYearPeak(endOfYearKapitalIndex: number): void {
    if (this.crisisDefinition === 'kapitalindex-drawdown') {
      // Update peak if not in crisis and end-of-year index is higher
      if (!this.crisisState.isInCrisis && endOfYearKapitalIndex > this.crisisState.precrisisKapitalIndex) {
        this.crisisState.precrisisKapitalIndex = endOfYearKapitalIndex;
      }
    }
  }

  /**
   * Negative return crisis detection
   * Crisis when annual return is negative (any negative value)
   */
  private detectNegativeReturnCrisis(
    investitionstopfRate: number,
    currentYear: number = 0,
    precrisisKapitalIndex: number = 100
  ): boolean {
    const isCrisis = investitionstopfRate < 0; // Any negative return triggers crisis

    if (isCrisis && !this.crisisState.isInCrisis) {
      // BUG FIX: Pass the actual crisis year and the capital index before the crash
      // (previously hardcoded 0/0, which leaked into the JSON export)
      this.startCrisis(currentYear, precrisisKapitalIndex, 'negative-return');
    } else if (!isCrisis && this.crisisState.isInCrisis) {
      this.endCrisis();
    }

    return isCrisis;
  }

  /**
   * Capital Index Drawdown Crisis Detection
   * Crisis starts when index drops below recent peak and continues until full recovery
   */
  private detectKapitalIndexDrawdownCrisis(
    currentYear: number,
    currentKapitalIndex: number,
    _previousKapitalIndex: number // Prefix with underscore to indicate intentionally unused
  ): boolean {
    // Handle first year - initialize peak, but STILL check for crisis at end of year
    if (currentYear === 0) {
      // Set initial peak (will be 100 typically)
      if (this.crisisState.precrisisKapitalIndex === 0) {
        this.crisisState.precrisisKapitalIndex = 100; // Start with initial index
      }
      
      // BUG FIX v2.4.5c: For end-of-year evaluation in year 0, check if index dropped
      // If current index < starting index (100), it's a crisis even in year 0
      if (currentKapitalIndex < this.crisisState.precrisisKapitalIndex) {
        this.startCrisis(currentYear, this.crisisState.precrisisKapitalIndex, 'kapitalindex-drawdown');
        return true;
      }
      
      // Update peak if index went up
      if (currentKapitalIndex > this.crisisState.precrisisKapitalIndex) {
        this.crisisState.precrisisKapitalIndex = currentKapitalIndex;
      }
      
      return false;
    }

    // Floating-point tolerance: 0.01 point difference (0.01% of 100 baseline)
    // This prevents spurious crisis triggers due to rounding errors
    const EPSILON = 0.01;
    
    // Update running peak if we're not in crisis
    if (!this.crisisState.isInCrisis) {
      // Check if we have a new peak (with tolerance)
      if (currentKapitalIndex > this.crisisState.precrisisKapitalIndex + EPSILON) {
        this.crisisState.precrisisKapitalIndex = currentKapitalIndex;
      }

      // Start crisis if meaningful decline from peak (Kapitalindex-Rückgang)
      // Only trigger if decline is greater than floating-point noise
      if (currentKapitalIndex < this.crisisState.precrisisKapitalIndex - EPSILON) {
        this.startCrisis(currentYear, this.crisisState.precrisisKapitalIndex, 'kapitalindex-drawdown');
        return true;
      }

      return false;
    }

    // We are in crisis - check for recovery (with tolerance)
    // Consider recovered if we're at or above peak (within tolerance)
    if (currentKapitalIndex >= this.crisisState.precrisisKapitalIndex - EPSILON) {
      this.endCrisis();
      // CRITICAL: After ending crisis, update peak to current value if it's higher
      // This prevents using stale peaks when checking for the next crisis
      if (currentKapitalIndex > this.crisisState.precrisisKapitalIndex + EPSILON) {
        this.crisisState.precrisisKapitalIndex = currentKapitalIndex;
      }
      return false;
    }

    // Still in crisis
    return true;
  }

  /**
   * Capital Index Drawdown Crisis Detection (Start of Year)
   * TIMING FIX: Uses start-of-year capital index for strategy decisions
   */
  private detectKapitalIndexDrawdownCrisisStartOfYear(
    currentYear: number,
    startOfYearKapitalIndex: number
  ): boolean {
    // Floating-point tolerance: same as end-of-year detection
    const EPSILON = 0.01;
    
    // Handle first year - no prior information available
    if (currentYear === 0) {
      this.crisisState.precrisisKapitalIndex = startOfYearKapitalIndex;
      return false;
    }

    // Update running peak if we're not in crisis (based on previous year's end-of-year data)
    if (!this.crisisState.isInCrisis) {
      // Check if previous year's end created a new peak (with tolerance)
      if (startOfYearKapitalIndex > this.crisisState.precrisisKapitalIndex + EPSILON) {
        this.crisisState.precrisisKapitalIndex = startOfYearKapitalIndex;
      }

      // Start crisis if start-of-year index is meaningfully below peak
      if (startOfYearKapitalIndex < this.crisisState.precrisisKapitalIndex - EPSILON) {
        this.startCrisis(currentYear, this.crisisState.precrisisKapitalIndex, 'kapitalindex-drawdown');
        return true;
      }

      return false;
    }

    // We are in crisis - check for recovery based on start-of-year index (with tolerance)
    // IMPORTANT: Crisis continues until start-of-year index recovers above pre-crisis peak
    if (startOfYearKapitalIndex >= this.crisisState.precrisisKapitalIndex - EPSILON) {
      this.endCrisis();
      // CRITICAL: After ending crisis, update peak to current value if it's higher
      // This prevents using stale peaks when checking for the next crisis
      if (startOfYearKapitalIndex > this.crisisState.precrisisKapitalIndex + EPSILON) {
        this.crisisState.precrisisKapitalIndex = startOfYearKapitalIndex;
      }
      return false;
    }

    // Still in crisis
    return true;
  }

  /**
   * Initialize or start a new crisis period
   */
  private startCrisis(
    year: number, 
    precrisisIndex: number, 
    crisisType: CrisisDefinitionType
  ): void {
    this.crisisState = {
      isInCrisis: true,
      crisisStartYear: year,
      precrisisKapitalIndex: precrisisIndex,
      crisisType: crisisType
    };
  }

  /**
   * End the current crisis period
   */
  private endCrisis(): void {
    this.crisisState = {
      ...this.crisisState,
      isInCrisis: false
    };
  }

  /**
   * Reset crisis state to initial conditions
   */
  private resetCrisisState(): void {
    this.crisisState = {
      isInCrisis: false,
      crisisStartYear: 0,
      precrisisKapitalIndex: 100, // Starting capital index
      crisisType: this.crisisDefinition
    };
    this.kapitalIndexPeaks = [];
  }

  /**
   * Track capital index history for analysis
   */
  private updateKapitalIndexHistory(year: number, kapitalIndex: number): void {
    // Ensure array is large enough
    while (this.kapitalIndexPeaks.length <= year) {
      this.kapitalIndexPeaks.push(100);
    }
    this.kapitalIndexPeaks[year] = kapitalIndex;
  }

  /**
   * Get current crisis state (for external components)
   */
  public getCrisisState(): CrisisState {
    return { ...this.crisisState };
  }

  /**
   * Restore crisis state (used to undo temporary evaluations)
   * BUG FIX v2.4.5: Prevents crisis state pollution between end-of-year recording and start-of-year decisions
   *
   * BUG FIX (Re-Audit K1): Fully restore the saved state, INCLUDING the peak
   * (precrisisKapitalIndex). The former "BUG FIX v2.4.10: never roll back to a lower peak"
   * caused a look-ahead bias in the `kapitalindex-drawdown` mode: `getMarktSituation`
   * runs the END-of-year evaluation of year N (which raises the peak to year N's
   * end-of-year index) BEFORE the START-of-year decision of the SAME year N. Keeping the
   * higher peak on rollback leaked year N's end-of-year information into the start-of-year
   * decision, so in rising markets every all-time-high year was mis-detected as the start
   * of a crisis (isGoodMarket=false). This temporary evaluation MUST be undone completely.
   *
   * The v2.4.10 concern (losing a legitimate new peak high) is handled elsewhere and
   * remains covered: the real, committed peak update for year N happens AFTER
   * processYearLogic via updateEndOfYearCrisisState -> updateEndOfYearPeak
   * (BaseSimulationEngine), i.e. at the start of the next iteration the peak is correctly
   * re-derived from the completed years 0..N. The peak bump inside getMarktSituation is
   * purely temporary and must not survive the rollback.
   */
  public restoreCrisisState(state: CrisisState): void {
    this.crisisState = { ...state };
  }

  /**
   * Get crisis definition being used
   */
  public getCrisisDefinition(): CrisisDefinitionType {
    return this.crisisDefinition;
  }

  /**
   * Update crisis definition (useful for testing or mode switching)
   */
  public setCrisisDefinition(newDefinition: CrisisDefinitionType): void {
    this.crisisDefinition = newDefinition;
    this.resetCrisisState();
  }

  /**
   * Calculate current drawdown from peak (useful for analysis)
   */
  public getCurrentDrawdown(currentKapitalIndex: number): number {
    if (this.crisisState.precrisisKapitalIndex === 0) {return 0;}
    return (this.crisisState.precrisisKapitalIndex - currentKapitalIndex) / 
           this.crisisState.precrisisKapitalIndex;
  }

  /**
   * Check if currently in a crisis period
   */
  public isInCrisis(): boolean {
    return this.crisisState.isInCrisis;
  }

  /**
   * Get crisis duration (years since crisis started)
   */
  public getCrisisDuration(currentYear: number): number {
    if (!this.crisisState.isInCrisis) {return 0;}
    return currentYear - this.crisisState.crisisStartYear;
  }
}
